import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

/**
 * ScopeSelector — SYSTEM-only strip that lets a super-user narrow a
 * Directory list to a single account (or one of two scope buckets).
 *
 * Renders nothing (returns null) when `hidden` is true — used to hide
 * the whole control for ACCOUNT_ADMIN callers without wrapping the
 * consumer in a conditional.
 *
 * Two-part control:
 *   1. A scope <Select> — the choices depend on the `variant`:
 *
 *      variant="users"   → "all" | "account"
 *        ("All accounts" default | "By account")
 *      variant="groups"  → "all" | "system" | "account"
 *        ("All scopes" | "System-scoped" | "By account")
 *      variant="roles"   → "all" | "predefined" | "account"
 *        ("All" | "System-defined" | "Account custom")
 *
 *   2. When scope==="account" (any variant), an Autocomplete picker over
 *      the caller-supplied `accounts` list. Selecting one sets
 *      `accountId` in the value payload; clearing it drops back to
 *      "all" so a bad partial state can't survive.
 *
 * `value` is the settled selection: `{ scope, accountId }` — parents
 * carry it in URL query params via useSearchParams so refresh keeps
 * state.
 *
 * `onChange` fires with a fresh `{ scope, accountId }` payload on any
 * change (scope switch or account pick).
 *
 * Props:
 *   variant  : "users" | "groups" | "roles" — chooses the option set
 *   value    : { scope, accountId } — current selection
 *   onChange : ({ scope, accountId }) => void
 *   accounts : [{ id, name }] — options for the account picker;
 *              pass null while still loading to show a "Loading…" hint
 *   hidden   : true → render nothing (ACCOUNT_ADMIN case)
 */

const VARIANT_OPTIONS = {
  users: [
    { value: "all", label: "All accounts" },
    { value: "account", label: "By account" },
  ],
  groups: [
    { value: "all", label: "All scopes" },
    { value: "system", label: "System-scoped" },
    { value: "account", label: "By account" },
  ],
  roles: [
    { value: "all", label: "All" },
    { value: "predefined", label: "System-defined" },
    { value: "account", label: "Account custom" },
  ],
};

function scopeIcon(scope) {
  if (scope === "system" || scope === "predefined")
    return <PublicOutlinedIcon fontSize="small" />;
  if (scope === "account") return <BusinessOutlinedIcon fontSize="small" />;
  return null;
}

export default function ScopeSelector({
  variant = "users",
  value,
  onChange,
  accounts,
  hidden = false,
  sx,
}) {
  const scope = value?.scope || "all";
  const accountId = value?.accountId || null;

  // Local echo of the selected account object so the Autocomplete keeps
  // rendering the name even before `accounts` loads.
  const [pickerVal, setPickerVal] = useState(null);
  useEffect(() => {
    if (!accountId || !Array.isArray(accounts)) {
      // Preserve the placeholder object we may have seeded from URL
      // params so the picker doesn't blank out mid-load.
      if (!accountId) setPickerVal(null);
      return;
    }
    const hit = accounts.find((a) => a.id === accountId);
    if (hit) setPickerVal(hit);
    else if (!pickerVal || pickerVal.id !== accountId) {
      // Unknown id — show it verbatim so the caller sees something is
      // pinned even if the row is stale/tombstoned.
      setPickerVal({ id: accountId, name: accountId });
    }
    // pickerVal intentionally excluded — re-syncing on every internal
    // set would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, accounts]);

  if (hidden) return null;

  const options = VARIANT_OPTIONS[variant] || VARIANT_OPTIONS.users;

  const emit = (next) => {
    if (typeof onChange === "function") onChange(next);
  };

  const handleScope = (nextScope) => {
    // Switching away from "account" always clears the pinned id so
    // the URL doesn't carry a stale accountId under the wrong scope.
    if (nextScope !== "account") {
      setPickerVal(null);
      emit({ scope: nextScope, accountId: null });
      return;
    }
    emit({ scope: nextScope, accountId });
  };

  const handleAccount = (_e, next) => {
    if (!next) {
      // Cleared — drop back to "all" so a scope="account" with no id
      // can't survive.
      setPickerVal(null);
      emit({ scope: "all", accountId: null });
      return;
    }
    setPickerVal(next);
    emit({ scope: "account", accountId: next.id });
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ sm: "center" }}
      sx={{ ...sx }}
      useFlexGap
      role="group"
      aria-label="Scope filter"
    >
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Scope
      </Typography>
      <Select
        size="small"
        value={scope}
        onChange={(e) => handleScope(e.target.value)}
        inputProps={{ "aria-label": "Scope" }}
        SelectDisplayProps={{ "aria-label": "Scope" }}
        renderValue={(v) => {
          const opt = options.find((o) => o.value === v) || options[0];
          const icon = scopeIcon(v);
          return (
            <Stack direction="row" spacing={0.75} alignItems="center">
              {icon}
              <span>{opt.label}</span>
            </Stack>
          );
        }}
        sx={{ minWidth: 180 }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {scopeIcon(o.value)}
              <span>{o.label}</span>
            </Stack>
          </MenuItem>
        ))}
      </Select>

      {scope === "account" && (
        <Box sx={{ minWidth: 260, flex: "1 1 220px", maxWidth: 360 }}>
          <Autocomplete
            size="small"
            options={Array.isArray(accounts) ? accounts : []}
            getOptionLabel={(o) => o?.name || o?.id || ""}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            value={pickerVal}
            onChange={handleAccount}
            loading={accounts === null}
            loadingText="Loading accounts…"
            noOptionsText="No accounts"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Pick an account…"
                aria-label="Account"
              />
            )}
          />
        </Box>
      )}
    </Stack>
  );
}
